import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import SiteNav from "components/Redesign/SiteNav.js";
import SiteFooter from "components/Redesign/SiteFooter.js";
import { addToCart, updateItems } from "views/examples/Shop/cartActions";
import { fetchAvailability, applyAvailability } from "views/examples/Shop/availability";
import VarietyIcon from "components/Redesign/VarietyIcon.js";

import photoFruehlingsbluete from "assets/img/product-photos/fruehlingsbluete.jpg";
import photoWald from "assets/img/product-photos/wald.jpg";
import photoPrimavera from "assets/img/product-photos/primavera.jpg";
import photoEstate from "assets/img/product-photos/estate.jpg";
import photoAlpenwiese from "assets/img/product-photos/alpenwiese.jpg";

// Zwei bewusst getrennte Werte pro Sorte:
//
//   intensity (1–5)  Geschmacksintensität als grobe Bewertung. Steuert die
//                    gefüllten Punkte auf der Sortenkarte.
//   scalePos (0–100) Position auf der Geschmacksskala in Prozent. Bildet die
//                    WAHRGENOMMENEN Abstände ab, nicht den linearen Wert:
//                    Estate ist zwar deutlich am kräftigsten, ein halber
//                    Skalenbalken Abstand zu Wald würde die übrigen Sorten
//                    aber unnötig zusammendrücken. Der Sprung zu Estate ist
//                    daher auf 35% gesetzt, davor stehen die vier anderen in
//                    gleichen Abständen.
//
// Wichtig: Beide Werte müssen dieselbe Reihenfolge ergeben. Die Prüfung
// weiter unten meldet es in der Entwicklung, falls das auseinanderläuft.
//
// season/location sind reine Sortierschlüssel für die Standard-Reihenfolge.
const CARDS = [
  { n: 1, category: "Frühlingsblüte", intensity: 2,   scalePos: 21.7, icon: "bloom",  season: "fruehjahr", location: "dachlissen", color: "var(--card-fruehlingsblute)", bg: "var(--card-fruehlingsblute)", theme: "dark", photo: photoFruehlingsbluete },
  { n: 2, category: "Wald",           intensity: 3,   scalePos: 65,   icon: "tree",   season: "sommer",    location: "dachlissen", color: "var(--card-wald-accent)",     bg: "var(--card-wald)",            theme: "light", photo: photoWald },
  { n: 3, category: "Primavera",      intensity: 1,   scalePos: 0,    icon: "branch", season: "fruehjahr", location: "malcantone", color: "var(--card-primavera)",       bg: "var(--card-primavera)",       theme: "dark", photo: photoPrimavera },
  { n: 4, category: "Estate",         intensity: 5,   scalePos: 100,  icon: "sun",    season: "sommer",    location: "malcantone", color: "var(--card-estate)",          bg: "var(--card-estate)",          theme: "dark", photo: photoEstate },
  { n: 5, category: "Alpenwiese",     intensity: 2.4, scalePos: 43.3, icon: "peak",   season: "sommer",    location: "falera",     color: "var(--card-alpenwiese)",      bg: "var(--card-alpenwiese)",      theme: "dark", photo: photoAlpenwiese },
];

// Standard-Reihenfolge: Standort ist das staerkere Kriterium — Tessin, dann
// Dachlissen, dann Falera. Innerhalb eines Standorts Frühjahr vor Sommer.
// Ergibt: Primavera, Estate, Frühlingsblüte, Wald, Alpenwiese.
const LOCATION_RANK = { malcantone: 0, dachlissen: 1, falera: 2 };
const SEASON_RANK = { fruehjahr: 0, sommer: 1 };

function defaultOrder(a, b) {
  return (LOCATION_RANK[a.location] - LOCATION_RANK[b.location])
    || (SEASON_RANK[a.season] - SEASON_RANK[b.season]);
}

// Skala und Karte dürfen nicht in unterschiedlicher Reihenfolge stehen.
if (process.env.NODE_ENV !== "production") {
  const byIntensity = CARDS.slice().sort((a, b) => a.intensity - b.intensity).map(c => c.n);
  const byPosition = CARDS.slice().sort((a, b) => a.scalePos - b.scalePos).map(c => c.n);
  if (byIntensity.join() !== byPosition.join()) {
    console.warn(
      "Geschmacksskala: intensity und scalePos ergeben unterschiedliche " +
      "Reihenfolgen. Punkte auf der Karte und Position auf der Skala " +
      "widersprechen sich.", { byIntensity, byPosition }
    );
  }
}

// Gefüllte Punkte auf der Karte: gerundeter Intensitätswert.
function filledDots(intensity) {
  return Math.round(intensity);
}

// Kompakte, am Seitenkopf angeheftete Geschmacksleiste. Sortenname und Signet
// stehen direkt unter dem jeweiligen Punkt — moeglich geworden durch die
// gleichmaessigeren Abstaende (siehe scalePos oben), bei denen selbst die
// engsten Nachbarn noch rund ein Fuenftel der Breite auseinanderliegen.
function TasteScale({ t, onSelect }) {
  const [activeN, setActiveN] = React.useState(null);

  // Beschriftungen an den Enden nach innen ausrichten, damit sie nicht
  // ueber die Achse hinausragen.
  const labelAlign = pos => (pos <= 2 ? " is-start" : pos >= 98 ? " is-end" : "");

  return (
    <div className="honig-scale">
      <div className="honig-scale-end">{t("honig.Scale mild label")}</div>

      <div className="honig-scale-track-wrap">
        <div className="honig-scale-track">
          {CARDS.map(card => (
            <button
              key={card.n}
              type="button"
              className={
                "honig-scale-marker" + labelAlign(card.scalePos) +
                (activeN === card.n ? " is-active" : "")
              }
              style={{ left: card.scalePos + "%" }}
              onMouseEnter={() => setActiveN(card.n)}
              onMouseLeave={() => setActiveN(null)}
              onFocus={() => setActiveN(card.n)}
              onBlur={() => setActiveN(null)}
              onClick={() => onSelect(card.n)}
              title={t("honig.Card " + card.n + " scale label")}
              aria-label={
                t("honig.Card " + card.n + " name") + " - " +
                t("honig.Card " + card.n + " scale label") + ". " +
                t("honig.Scale jump")
              }
            >
              <span className="honig-scale-dot" style={{ background: card.color }} />
              <span className="honig-scale-label">
                <VarietyIcon name={card.icon} size={15} />
                <span className="honig-scale-label-text">
                  {t("honig.Card " + card.n + " name")}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="honig-scale-end honig-scale-end-intense">
        {t("honig.Scale intense label")}
      </div>
    </div>
  );
}

function VarietyCard({ card, t, category, onAdd, flash }) {
  const items = category ? category.items : [];
  return (
    <article
      id={"honig-card-" + card.n}
      className={
        "honig-card honig-card-" + card.category.toLowerCase() +
        (card.theme === "dark" ? " honig-card-ondark" : "") +
        (flash ? " honig-card-flash" : "")
      }
      style={{ background: card.bg }}
    >
      <div className="honig-card-photo" style={{ background: card.bg }}>
        <img src={card.photo} alt={t("honig.Card " + card.n + " name")} loading="lazy" />
      </div>
      <div className="honig-card-body">
        <div className="honig-card-meta">
          {t("honig.Card " + card.n + " location")} · {t("honig.Card " + card.n + " altitude")} · {t("honig.Card " + card.n + " season")}
        </div>
        {/* Name und Intensitaet teilen sich eine Zeile — spart auf dem
            Desktop eine Zeile plus Abstand pro Karte. */}
        <div className="honig-card-headline">
          <h2>{t("honig.Card " + card.n + " name")}</h2>
          <div className="honig-card-scale-badge">
            <span className="honig-card-scale-dots" aria-hidden="true">
              {[1, 2, 3, 4, 5].map(i => (
                <span key={i} className={"dot" + (i <= filledDots(card.intensity) ? " dot-filled" : "")} />
              ))}
            </span>
            {t("honig.Card " + card.n + " scale label")}
          </div>
        </div>

        <div className="honig-card-story">
          <p>{t("honig.Card " + card.n + " story")}</p>
        </div>

        <dl className="honig-card-facts">
          <div>
            <dt>{t("honig.Texture heading")}</dt>
            <dd>{t("honig.Card " + card.n + " texture")}</dd>
          </div>
          <div>
            <dt>{t("honig.Aroma heading")}</dt>
            <dd>{t("honig.Card " + card.n + " aroma")}</dd>
          </div>
          <div>
            <dt>{t("honig.Pairing heading")}</dt>
            <dd>{t("honig.Card " + card.n + " pairing")}</dd>
          </div>
        </dl>

        <div className="honig-card-order">
          {items.map(item => {
            const soldOut = item.availability === "ausverkauft";
            return (
              <div className="honig-card-order-row" key={item.id}>
                <span className="honig-card-order-size">{item.size}</span>
                <span className="honig-card-order-price">{item.price.toFixed(2)} CHF</span>
                <button
                  type="button"
                  className={soldOut ? "honig-cart-btn honig-cart-btn-soldout" : "honig-cart-btn"}
                  disabled={soldOut}
                  onClick={() => onAdd(item, card)}
                  aria-label={
                    (soldOut ? t("honig.Sold out") : t("honig.Add to cart")) +
                    " " + t("honig.Card " + card.n + " name") + " " + item.size
                  }
                >
                  {soldOut ? t("honig.Sold out") : (
                    <>
                      <i className="fa fa-cart-arrow-down" aria-hidden="true" />
                      {/* Auf dem Telefon per CSS ausgeblendet — der aria-label
                          des Buttons traegt den Text weiterhin. */}
                      <span className="honig-cart-btn-label">{t("honig.Add to cart")}</span>
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </article>
  );
}

function UnserHonig() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const categories = useSelector(state => state.categories);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    // Beide Quellen parallel: products.json liefert das Sortiment, die
    // Google-Tabelle nur die aktuelle Verfuegbarkeit. Ist die Tabelle nicht
    // erreichbar, liefert fetchAvailability eine leere Map und die Werte
    // aus products.json bleiben stehen.
    Promise.all([
      fetch(process.env.PUBLIC_URL + "/products/products.json").then(r => r.json()),
      fetchAvailability(),
    ]).then(([data, availability]) => {
      dispatch(updateItems(data.items, applyAvailability(data.categories, availability)));
    });
  }, [dispatch]);

  // Verfuegbare Sorten zuerst, innerhalb beider Gruppen die Standard-
  // Reihenfolge (Fruehjahr vor Sommer, Tessin vor Dachlissen vor Falera).
  const sortedCards = React.useMemo(() => {
    const hasStock = card => {
      const cat = categories.find(c => c.title === card.category);
      const items = cat && cat.items ? cat.items : [];
      return items.some(i => i.availability !== "ausverkauft");
    };
    return CARDS.slice().sort(
      (a, b) => (Number(hasStock(b)) - Number(hasStock(a))) || defaultOrder(a, b)
    );
  }, [categories]);

  const [toast, setToast] = React.useState(null);
  const toastTimer = React.useRef(null);
  const [flashCard, setFlashCard] = React.useState(null);
  const flashTimer = React.useRef(null);

  React.useEffect(() => () => {
    clearTimeout(toastTimer.current);
    clearTimeout(flashTimer.current);
  }, []);

  // Klick auf einen Punkt der Geschmacksskala: zur Sortenkarte springen und
  // sie kurz hervorheben, damit klar ist, wo man gelandet ist.
  const handleScaleSelect = n => {
    const target = document.getElementById("honig-card-" + n);
    if (target) {
      const reduceMotion = window.matchMedia
        && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    }
    // Klasse erst entfernen, wenn die Animation durch ist: 0.35s Verzoegerung
    // plus 2.2s Laufzeit. Vorher abgeschnitten wirkt das Aufleuchten kaputt.
    setFlashCard(null);
    clearTimeout(flashTimer.current);
    // Ein Frame Pause, damit ein erneuter Klick auf dieselbe Karte die
    // Animation wirklich neu startet statt sie zu ignorieren.
    requestAnimationFrame(() => setFlashCard(n));
    flashTimer.current = setTimeout(() => setFlashCard(null), 2800);
  };

  // Die Meldung uebernimmt Hintergrund und Helligkeit der Sortenkarte, damit
  // sichtbar bleibt, welcher Honig gerade gelandet ist.
  const handleAdd = (item, card) => {
    dispatch(addToCart(item.id));
    setToast({
      id: Date.now(),
      label: t("honig.Card " + card.n + " name") + " " + item.size,
      bg: card.bg,
      theme: card.theme,
    });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4500);
  };

  return (
    <div className="rd">
      <SiteNav />

      <section className="page-shell page-shell-honig">
        <div className="wrap">
          <div className="rd-section-no">{t("honig.Page label")}</div>
          <h1 className="page-shell-h1">{t("honig.Page title")}</h1>
          <div className="honig-intro">
            {/* Bewusst ein Absatz: die beiden Saetze gehoeren zusammen. */}
            <p>{t("honig.Intro p1")} {t("honig.Intro p2")}</p>
          </div>

          <div className="honig-step-panel">
            {/* Ueberschrift bewusst ausserhalb der Leiste: sie darf
                wegscrollen, die Leiste selbst bleibt oben haengen. */}
            <div className="honig-scale-heading">
              <h2 className="honig-scale-title">{t("honig.Scale heading")}</h2>
              <span className="honig-scale-question">{t("honig.Scale question")}</span>
            </div>
            <TasteScale t={t} onSelect={handleScaleSelect} />

            <div className="honig-grid">
              {sortedCards.map(card => (
                <VarietyCard
                  key={card.n}
                  card={card}
                  t={t}
                  category={categories.find(c => c.title === card.category)}
                  onAdd={handleAdd}
                  flash={flashCard === card.n}
                />
              ))}
            </div>

            <div className="honig-cart-cta">
              <Link to="/warenkorb" className="cta-primary">
                {t("honig.Cart CTA")} <span className="arr">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* role=status meldet den Zugang auch Screenreadern, ohne den Fokus
          zu stehlen — der Nutzer kann ununterbrochen weiterstoebern. */}
      <div className="cart-toast-region" role="status" aria-live="polite">
        {toast && (
          <div
            className={"cart-toast cart-toast-" + toast.theme}
            style={{ background: toast.bg }}
            key={toast.id}
          >
            <span className="cart-toast-check" aria-hidden="true">✓</span>
            <span className="cart-toast-text">
              <strong>{toast.label}</strong> {t("honig.Toast added")}
            </span>
            <Link to="/warenkorb" className="cart-toast-link">
              {t("honig.Toast cta")} <span className="arr">→</span>
            </Link>
          </div>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}

export default UnserHonig;
